import { Zombie } from './Zombie.js'
import { Skeleton } from './Skeleton.js'
import { Spider } from './Spider.js'
import { Orc } from './Orc.js'
import { DarkKnight } from './DarkKnight.js'
import { ShadowWolf } from './ShadowWolf.js'
import { DarkWizard } from './DarkWizard.js'
import { DarkKing } from './DarkKing.js'
import { EmberGuard } from './EmberGuard.js'
import { CinderImp } from './CinderImp.js'

const ENEMY_CLASSES = {
  zombie: Zombie,
  skeleton: Skeleton,
  spider: Spider,
  orc: Orc,
  darkKnight: DarkKnight,
  shadowWolf: ShadowWolf,
  darkWizard: DarkWizard,
  emberGuard: EmberGuard,
  cinderImp: CinderImp,
  darkKing: DarkKing
}

export class EnemyManager {
  constructor(game, saveData = null) {
    this.game = game
    this.enemies = []
    this.deadEnemyIds = new Set()
    this.activatedEnemyIds = new Set()
    this.savedEnemyStates = new Map()
    this.allEnemiesCleared = false
    this.hasSpawnedAll = false
    
    if (saveData) {
      this.deserialize(saveData)
    }
  }
  
  update(delta) {
    if (this.game.world?.postgameVillage?.unlocked) {
      if (!this.allEnemiesCleared || this.enemies.length > 0) this.clearAllEnemies()
      return
    }

    // Spawn all enemies for all zones up front.
    if (!this.hasSpawnedAll && !this.allEnemiesCleared) {
      this.spawnAllZones()
      this.hasSpawnedAll = true
    }

    // Only update enemies near the player (extended viewport).
    const player = this.game.player
    const cullDist = 30 // tiles from player

    for (const enemy of this.enemies) {
      if (!enemy.isAlive) continue

      // Always advance death sequence so pending-death enemies don't get stuck.
      if (enemy.pendingDeath) {
        enemy.update(delta)
        continue
      }

      const roomActive = this.game.world.isRoomActiveAt(enemy.x, enemy.y)
      if (typeof enemy.setEncounterActive === 'function') {
        enemy.setEncounterActive(roomActive)
      }
      if (roomActive) {
        this.activatedEnemyIds.add(enemy.id)
      }

      const isActivated = this.activatedEnemyIds.has(enemy.id)
      // Enemies remember that their encounter has been activated, but they do
      // not simulate or chase through neighboring rooms while the player is
      // elsewhere. Pending deaths are advanced above this gate so their loot
      // and persistence state still finish correctly.
      if (!isActivated || !roomActive) {
        enemy.state = 'idle'
        enemy.targetPosition = null
        enemy.isMoving = false
        continue
      }

      const dx = enemy.x - player.x
      const dy = enemy.y - player.y
      if (Math.abs(dx) < cullDist && Math.abs(dy) < cullDist) {
        enemy.update(delta)
      }
    }
    
    // Apply night damage multiplier
    const damageMultiplier = this.game.dayNight.getEnemyDamageMultiplier()
    for (const enemy of this.enemies) {
      enemy.damageMultiplier = damageMultiplier
    }
  }
  
  spawnAllZones() {
    for (const zone of this.game.world.zones) {
      this.spawnEnemiesForZone(zone)
      this.spawnQuestEnemiesForZone(zone.name)
      this.spawnKeyChestEnemiesForZone(zone.name)
    }
    this.spawnBoss()
    this.spawnSavedBossMinions()
    this.savedEnemyStates.clear()
  }

  spawnBoss() {
    const spawn = this.game.world.bossSpawn
    if (!spawn || this.deadEnemyIds.has(spawn.id)) return null
    if (this.enemies.some(enemy => enemy.id === spawn.id)) return null
    const hasSavedState = this.savedEnemyStates.has(spawn.id)
    const boss = this.spawnEnemy(spawn.type, spawn.x, spawn.y, spawn.id)
    const retryHealth = this.game.world.darkKingRetryHealth
    if (boss && !hasSavedState && Number.isFinite(retryHealth) && retryHealth > 0) {
      boss.health = Math.min(boss.maxHealth, retryHealth)
    }
    return boss
  }
  
  spawnEnemiesForZone(zone) {
    if (this.allEnemiesCleared) return
    // Give zone access to game for solid checks
    zone.game = this.game
    
    for (let i = 0; i < zone.enemyCount; i++) {
      const typeIndex = i % zone.enemyTypes.length
      const enemyType = zone.enemyTypes[typeIndex]
      const EnemyClass = ENEMY_CLASSES[enemyType]
      
      if (!EnemyClass) continue
      
      const id = `${zone.name}_${enemyType}_${i}`
      
      if (this.deadEnemyIds.has(id)) continue
      
      const position = zone.getRandomPosition()
      
      const enemy = new EnemyClass(this.game, position.x, position.y, id)
      this.restoreEnemyState(enemy)
      this.enemies.push(enemy)
    }
  }
  
  spawnEnemy(type, x, y, id) {
    // Wizards belong exclusively to the Graveyard, including its quest rooms.
    // Keep this rule at the spawn boundary so future encounters cannot place
    // one in another level by mistake.
    if (
      type === 'darkWizard' &&
      this.game.world?.getZoneAt(x, y)?.name !== 'graveyard'
    ) return null

    const EnemyClass = ENEMY_CLASSES[type]
    if (!EnemyClass) return null
    
    const enemy = new EnemyClass(this.game, x, y, id)
    this.restoreEnemyState(enemy)
    this.enemies.push(enemy)
    return enemy
  }

  restoreEnemyState(enemy) {
    const saved = this.savedEnemyStates.get(enemy.id)
    if (!saved) return false
    this.savedEnemyStates.delete(enemy.id)

    if (Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
      enemy.x = saved.x
      enemy.y = saved.y
    }
    if (Number.isFinite(saved.health) && saved.health > 0) {
      enemy.health = Math.min(enemy.maxHealth, saved.health)
    }
    if (saved.pendingDeath === true) {
      enemy.health = 0
      enemy.pendingDeath = true
      enemy.knockbackTimer = 0
    }
    return true
  }

  spawnSavedBossMinions() {
    const minions = []
    for (const saved of this.savedEnemyStates.values()) {
      if (
        typeof saved?.id !== 'string' ||
        !saved.id.startsWith('king_minion_') ||
        typeof saved.type !== 'string' ||
        this.deadEnemyIds.has(saved.id) ||
        this.enemies.some(enemy => enemy.id === saved.id) ||
        !Number.isFinite(saved.x) ||
        !Number.isFinite(saved.y)
      ) continue

      const minion = this.spawnEnemy(saved.type, saved.x, saved.y, saved.id)
      if (minion) minions.push(minion)
    }

    const boss = this.enemies.find(enemy => enemy.type === 'darkKing' && enemy.isAlive)
    if (boss && Array.isArray(boss.summonedMinions)) {
      boss.summonedMinions.push(...minions)
    }
  }

  spawnQuestEnemiesForZone(areaId) {
    if (this.allEnemiesCleared) return

    const branches = this.game.world.getQuestBranchesForArea(areaId)
    for (const branch of branches) {
      for (const spawn of branch.enemySpawns) {
        if (this.deadEnemyIds.has(spawn.id)) continue
        if (this.enemies.some(enemy => enemy.id === spawn.id)) continue
        this.spawnEnemy(spawn.type, spawn.x, spawn.y, spawn.id)
      }
    }
  }

  spawnKeyChestEnemiesForZone(areaId) {
    if (this.allEnemiesCleared) return
    const keyChests = this.game.world.keyChests?.filter(chest => chest.areaId === areaId) || []
    for (const chest of keyChests) {
      for (const spawn of chest.enemySpawns || []) {
        if (this.deadEnemyIds.has(spawn.id)) continue
        if (this.enemies.some(enemy => enemy.id === spawn.id)) continue
        this.spawnEnemy(spawn.type, spawn.x, spawn.y, spawn.id)
      }
    }
  }

  hasLivingEnemiesInRoom(x, y) {
    const roomId = this.game.world.getRoomIdForPosition(x, y)
    if (!roomId) return false

    return this.enemies.some(enemy => (
      enemy.isAlive &&
      !enemy.pendingDeath &&
      this.game.world.getRoomIdForPosition(enemy.x, enemy.y) === roomId
    ))
  }

  clearEnemiesInRoom(x, y) {
    const roomId = this.game.world.getRoomIdForPosition(x, y)
    if (!roomId) return 0
    let cleared = 0
    this.enemies = this.enemies.filter(enemy => {
      if (enemy.type === 'darkKing') return true
      if (this.game.world.getRoomIdForPosition(enemy.x, enemy.y) !== roomId) return true
      enemy.isAlive = false
      if (enemy.id) this.deadEnemyIds.add(enemy.id)
      cleared += 1
      return false
    })
    return cleared
  }
  
  removeEnemy(enemy) {
    const index = this.enemies.indexOf(enemy)
    if (index !== -1) {
      this.enemies.splice(index, 1)
    }
  }

  getZoneIndexForPosition(x, y) {
    const zone = this.game.world.getZoneAt(x, y)
    if (!zone) return -1
    return this.game.world.zones.findIndex(z => z.name === zone.name)
  }

  respawnAheadFromPosition(x, y) {
    const startIndex = this.getZoneIndexForPosition(x, y)
    if (startIndex < 0) return

    const zonesAhead = this.game.world.zones.slice(startIndex)
    const aheadNames = new Set(zonesAhead.map(z => z.name))

    this.enemies = this.enemies.filter(enemy => {
      const zone = this.game.world.getZoneAt(enemy.x, enemy.y)
      return zone && !aheadNames.has(zone.name)
    })

    this.deadEnemyIds = new Set(
      Array.from(this.deadEnemyIds).filter(id => !zonesAhead.some(z => id.startsWith(`${z.name}_`)))
    )

    this.activatedEnemyIds = new Set(
      Array.from(this.activatedEnemyIds).filter(id => !zonesAhead.some(z => id.startsWith(`${z.name}_`)))
    )

    this.allEnemiesCleared = false

    for (const zone of zonesAhead) {
      this.spawnEnemiesForZone(zone)
      this.spawnQuestEnemiesForZone(zone.name)
      this.spawnKeyChestEnemiesForZone(zone.name)
    }
    this.spawnBoss()
  }

  clearAllEnemies() {
    for (const enemy of this.enemies) {
      enemy.isAlive = false
    }
    this.enemies = []
    this.allEnemiesCleared = true
  }
  
  markEnemyDead(enemy) {
    this.deadEnemyIds.add(enemy.id)
  }
  
  render(renderer) {
    const bounds = renderer.getVisibleBounds()
    const pad = 3 // slight padding beyond viewport
    const minX = bounds.minX - pad
    const maxX = bounds.maxX + pad
    const minY = bounds.minY - pad
    const maxY = bounds.maxY + pad

    for (const enemy of this.enemies) {
      if (!enemy.isAlive) continue
      if (!this.game.world.isPositionInActiveLevel(enemy.x, enemy.y)) continue
      if (enemy.x < minX || enemy.x > maxX || enemy.y < minY || enemy.y > maxY) continue
      if (!this.game.world.isTileRevealed(enemy.x, enemy.y)) continue
      enemy.render(renderer)
    }
  }
  
  serialize() {
    const deadEnemyIds = new Set(this.deadEnemyIds)
    const enemyStates = []
    for (const enemy of this.enemies) {
      if (!enemy.isAlive) {
        if (enemy.id) deadEnemyIds.add(enemy.id)
        continue
      }
      // Preserve a lethal hit that is still completing its brief knockback.
      // Restoring it as pending lets the regular death path award loot and, for
      // the Dark King, finish victory instead of resurrecting or soft-locking.
      enemyStates.push({
        ...enemy.serialize(),
        health: enemy.pendingDeath || enemy.health <= 0 ? 0 : enemy.health,
        pendingDeath: enemy.pendingDeath || enemy.health <= 0
      })
    }

    return {
      deadEnemyIds: Array.from(deadEnemyIds),
      activatedEnemyIds: Array.from(this.activatedEnemyIds),
      enemyStates,
      allEnemiesCleared: this.allEnemiesCleared
    }
  }
  
  deserialize(data) {
    if (data) {
      const rebuildSpawnedEnemies = this.hasSpawnedAll
      this.deadEnemyIds = new Set(
        (Array.isArray(data.deadEnemyIds) ? data.deadEnemyIds : [])
          .filter(id => typeof id === 'string')
      )
      this.activatedEnemyIds = new Set(
        (Array.isArray(data.activatedEnemyIds) ? data.activatedEnemyIds : [])
          .filter(id => typeof id === 'string')
      )
      this.savedEnemyStates = new Map(
        (Array.isArray(data.enemyStates) ? data.enemyStates : [])
          .filter(state => (
            typeof state?.id === 'string' &&
            typeof state.type === 'string' &&
            state.isAlive !== false &&
            Number.isFinite(state.health) &&
            (state.health > 0 || state.pendingDeath === true)
          ))
          .map(state => [state.id, { ...state }])
      )
      this.allEnemiesCleared = data.allEnemiesCleared === true

      if (rebuildSpawnedEnemies) {
        this.enemies = []
        if (!this.allEnemiesCleared) this.spawnAllZones()
        this.hasSpawnedAll = true
      }
    }
  }
}
